import type { Route } from "next"

/**
 * This rebuilds the url from searchParams, for cases where we need to redirect keeping something that exists
 * @param searchParams the params that already exist in the page
 * @param page the path of the page you want to send the user, keep nothing if you want to default to root
 * @param inject parameters to inject in case we have something to add to what's being rebuilt
 */
export const rebuildUrlParams = (
  searchParams: URLSearchParams,
  page: string = "/",
  inject?: Array<[string, string | null | undefined]>
): Route => {
  const parameters: Array<{ key: string; value: string }> = []

  // Skip keys that will be injected to avoid duplicates in the URL
  const injectKeys = new Set(inject?.map(([k]) => k) ?? [])

  if (searchParams) {
    for (const [key, value] of searchParams) {
      if (key === "to") {
        page = value
      } else if (!injectKeys.has(key)) {
        parameters.push({ key, value })
      }
    }
  }

  if (inject) {
    for (const element of inject) {
      if (element[1]) {
        parameters.push({
          key: element[0],
          value: element[1],
        })
      }
    }
  }

  let rebuiltParams = ""

  for (let i = 0; i < parameters.length; i++) {
    const char = i === 0 ? "?" : "&"
    const item = parameters[i]
    rebuiltParams = rebuiltParams + char + item.key + "=" + item.value
  }

  const destination = `${page}${rebuiltParams}`

  return destination as Route
}

/**
 * Tracking identity params that were part of the removed gtm/ga/exp
 * URL-forcing feature. They now live only in cookies and must never be
 * propagated into outgoing/rebuilt URLs.
 */
export const FORCED_TRACKING_PARAMS = ["gtm", "ga", "exp"]

/**
 * Return a copy of the given search params with the removed URL-forcing
 * tracking keys (gtm/ga/exp) stripped, so an inbound `?gtm=&ga=&exp=` on the
 * current page is never forwarded into a rebuilt mirror/checkout URL.
 *
 * @param searchParams - the current page search params
 * @returns a new URLSearchParams without the forced tracking keys
 */
export const stripForcedTrackingParams = (
  searchParams: URLSearchParams
): URLSearchParams => {
  const cleaned = new URLSearchParams(searchParams.toString())
  for (const key of FORCED_TRACKING_PARAMS) {
    cleaned.delete(key)
  }
  return cleaned
}
